import Role from "@/constants/Role";
import {
  Class,
  Staff,
  Term,
  All,
  RolePage,
  ScoreReport,
  AttendanceReport,
  GraduationCategory,
  GraduationStudents,
  Program,
  Calendar,
  Communication,
  Attendance,
  Subject,
  GradeScale,
  Student,
  IndividualAssessment,
  OverallAssessment,
  ExtendedExportation,
  ProgressReport,
  Survey,
  TaskManagement,
  ZoomMeeting
} from "@/constants/Permissions";
import { hasValidRole } from "@/helpers/role-helper";
import { can, passesIgscTranscriptRule } from "@/helpers/permission";
import { store } from "@/store";

export const staff = {
  staffList: function() {
    if (
      can(Staff.actions.list, Staff.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  staffAdd: function() {
    if (
      can(Staff.actions.add, Staff.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  staffDetail: function() {
    return true;
  }
};

export const term = {
  termList: function() {
    return can(Term.actions.list, Term.subject);
  },
  termDetail: function() {
    return (
      can(Term.actions.view, Term.subject) ||
      can(Term.actions.edit, Term.subject)
    );
  }
};

export const graduation = {
  list: function() {
    return can(GraduationCategory.actions.list, GraduationCategory.subject);
  },
  detail: function() {
    return (
      can(GraduationCategory.actions.view, GraduationCategory.subject) ||
      can(GraduationCategory.actions.edit, GraduationCategory.subject)
    );
  }
};

export const program = {
  list: function() {
    if (
      can(Program.actions.list, Program.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  add: function() {
    if (
      can(Program.actions.add, Program.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  detail: function() {
    if (
      can(Program.actions.view, Program.subject) ||
      can(Program.actions.edit, Program.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  }
};

export const subject = {
  list: function() {
    if (
      can(Subject.actions.list, Subject.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  add: function() {
    if (
      can(Subject.actions.add, Subject.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  detail: function() {
    return (
      can(Subject.actions.view, Subject.subject) ||
      can(Subject.actions.edit, Subject.subject)
    );
  }
};

export const student = {
  list: function() {
    return can(Student.actions.list, Student.subject);
  },
  add: function() {
    return can(Student.actions.add, Student.subject);
  },
  detail: function(userCfg) {
    return (
      can(Student.actions.view, Student.subject) ||
      can(Class.actions.view, Class.subject) ||
      can(Class.actions.edit, Class.subject) ||
      hasValidRole(userCfg.userRole, [Role.Counselor, Role.Teacher])
    );
  },
  transcript: function(userCfg) {
    // IGSC restricts transcripts to the Admin role
    return (
      passesIgscTranscriptRule(userCfg?.userRole) &&
      can(Student.actions.viewTranscript, Student.subject)
    );
  }
};

export const classes = {
  list: function() {
    if (
      can(Class.actions.list, Class.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  add: function() {
    if (
      can(Class.actions.add, Class.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  detail: function() {
    return (
      can(Class.actions.view, Class.subject) ||
      can(Class.actions.edit, Class.subject)
    );
  }
};

export const gradeScale = {
  list: function() {
    if (
      can(GradeScale.actions.list, GradeScale.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  add: function() {
    if (
      can(GradeScale.actions.add, GradeScale.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  detail: function() {
    return (
      can(GradeScale.actions.view, GradeScale.subject) ||
      can(GradeScale.actions.edit, GradeScale.subject)
    );
  }
};

export const schedule = {
  list: function() {
    return can(Calendar.actions.list, Calendar.subject);
  },
  attendance: function() {
    return (
      can(Attendance.actions.view, Attendance.subject) ||
      can(Attendance.actions.edit, Attendance.subject)
    );
  }
};

export const communication = {
  detail: function() {
    return (
      can(Communication.actions.sendMail, Communication.subject) ||
      can(Communication.actions.sendAnnouncement, Communication.subject) ||
      can(Communication.actions.template, Communication.subject) ||
      can(Communication.actions.sendSms, Communication.subject)
    );
  }
};

export const individualAssessmentReport = {
  detail: function() {
    return (
      can(IndividualAssessment.actions.view, IndividualAssessment.subject) ||
      can(Student.actions.view, Student.subject)
    );
  }
};

export const overallAssessmentReport = {
  detail: function() {
    return can(OverallAssessment.actions.view, OverallAssessment.subject);
  }
};

export const counselor = {
  list: function(userCfg) {
    if (hasValidRole(userCfg.userRole, [Role.Student, Role.Family])) {
      return true;
    }
    return false;
  }
};

export const scoreReport = {
  list: function() {
    if (
      can(ScoreReport.actions.view, ScoreReport.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  }
};

export const academic = {
  list: function(userCfg) {
    if (hasValidRole(userCfg.userRole, [Role.Student, Role.Family])) {
      return true;
    }
    return false;
  }
};

export const role = {
  list: function() {
    if (
      can(RolePage.actions.list, RolePage.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  add: function() {
    if (
      can(RolePage.actions.add, RolePage.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  },
  detail: function() {
    if (
      can(RolePage.actions.view, RolePage.subject) ||
      can(RolePage.actions.edit, RolePage.subject)
    ) {
      return true;
    }
    return false;
  }
};

export const family = {
  accDetail: function(userCfg) {
    if (hasValidRole(userCfg.userRole, Role.Family)) {
      return true;
    }
    return false;
  }
};

export const attendanceReport = {
  list: function() {
    if (
      can(AttendanceReport.actions.view, AttendanceReport.subject) ||
      can(All.actions, All.subject)
    ) {
      return true;
    }
    return false;
  }
};

export const assignStudent = {
  list: function(userCfg) {
    if (hasValidRole(userCfg.userRole, [Role.Teacher, Role.Counselor])) {
      return true;
    }
    return false;
  }
};

export const dashboard = {
  checkAll: function() {
    return true;
  }
};
export const notification = {
  list: function() {
    return true;
  }
};

export const actionLogs = {
  list: function() {
    return can(All.actions, All.subject);
  }
};
export const graduated = {
  list: function() {
    return can(GraduationStudents.actions.list, GraduationStudents.subject);
  }
};
export const schoolParameter = {
  list: function() {
    if (can(All.actions, All.subject)) {
      return true;
    }
    return false;
  }
};
export const myProfile = {
  detail: function() {
    return true;
  }
};
export const extendedExportation = {
  export: function() {
    return can(ExtendedExportation.actions.export, ExtendedExportation.subject);
  }
};

export const progressReport = {
  detail: function() {
    const userInfo = store.getters["user/info"];
    const isAuthorized = userInfo.inputterOrReviewDetail?.authorized;
    return (
      can(ProgressReport.actions.setting, ProgressReport.subject) ||
      can(ProgressReport.actions.totalReport, ProgressReport.subject) ||
      can(ProgressReport.actions.submittedReport, ProgressReport.subject) ||
      can(
        ProgressReport.actions.censorSubmittedReport,
        ProgressReport.subject
      ) ||
      isAuthorized
    );
  },
  detailProgress: function(userCfg) {
    const userInfo = store.getters["user/info"];
    const isAuthorized = userInfo.inputterOrReviewDetail?.authorized;
    return (
      isAuthorized === "Inputter" ||
      hasValidRole(userCfg.userRole, [Role.Admin, Role.Principal])
    );
  }
};
export const survey = {
  list: function(userCfg) {
    return (
      can(Survey.actions.list, Survey.subject) ||
      can(All.actions, All.subject) ||
      hasValidRole(userCfg.userRole, Role.Teacher)
    );
  },
  add: function(userCfg) {
    return (
      can(Survey.actions.add, Survey.subject) ||
      can(All.actions, All.subject) ||
      hasValidRole(userCfg.userRole, Role.Teacher)
    );
  },
  detail: function(userCfg) {
    return (
      can(Survey.actions.view, Survey.subject) ||
      can(Survey.actions.edit, Survey.subject) ||
      hasValidRole(userCfg.userRole, Role.Teacher)
    );
  },
  clone: function(userCfg) {
    return (
      can(Survey.actions.copy, Survey.subject) ||
      hasValidRole(userCfg.userRole, Role.Teacher)
    );
  }
};
//fix me when have permission
export const surveyReport = {
  detail: function() {
    return true;
  }
};

export const taskManagement = {
  detail: function(userCfg) {
    return !hasValidRole(userCfg.userRole, [Role.Student, Role.Family]);
  },
  add: function() {
    return can(TaskManagement.actions.add, TaskManagement.subject);
  }
};

export const zoomMeeting = {
  list: function(userCfg) {
    return (
      can(ZoomMeeting.actions.list, ZoomMeeting.subject) ||
      can(All.actions, All.subject) ||
      hasValidRole(userCfg.userRole, Role.Teacher)
    );
  },
  add: function(userCfg) {
    return (
      can(ZoomMeeting.actions.add, ZoomMeeting.subject) ||
      can(All.actions, All.subject) ||
      hasValidRole(userCfg.userRole, Role.Teacher)
    );
  },
  report: function(userCfg) {
    return (
      can(ZoomMeeting.actions.report, ZoomMeeting.subject) ||
      can(All.actions, All.subject) ||
      hasValidRole(userCfg.userRole, Role.Teacher)
    );
  },
  updateReport: function(userCfg) {
    return (
      can(ZoomMeeting.actions.updateReport, ZoomMeeting.subject) ||
      can(All.actions, All.subject) ||
      hasValidRole(userCfg.userRole, Role.Teacher)
    );
  },
  detail: function(userCfg) {
    return (
      can(ZoomMeeting.actions.view, ZoomMeeting.subject) ||
      can(ZoomMeeting.actions.edit, ZoomMeeting.subject) ||
      hasValidRole(userCfg.userRole, Role.Teacher)
    );
  }
};
